import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import React, { Component } from 'react';
import { connect, MapStateToProps } from 'react-redux';

import { deleteProgram, loadProgram, setProgramSelectedWeek } from '../../actions/programs';
import { ProgramCatalogEntry, ProgramResponse } from '../../api/programs.interface';
import { withRouter, WithRouterProps } from '../../common/hooks/withParams';
import { ReduxState } from '../../reducers';
import DeleteProgramDialog from './DeleteProgramDialog';
import ProgramWeekGrid from './ProgramWeekGrid';
import ProgramWeekNavigator from './ProgramWeekNavigator';
import { buildProgramSchedulePath, createProgramDeleteSummary, countProgramDays, getProgramNextWeekNumber, PROGRAM_COMPLETION_ACTION_LABELS } from './programs.utils';

interface ComponentProps {}

interface ReduxProps {
    deletingProgramName?: string;
    loadingProgram: boolean;
    selectedProgramCatalog?: ProgramCatalogEntry;
    selectedProgram?: ProgramResponse;
    selectedWeek: number;
}

interface ReduxActionProps {
    deleteProgram: (name: string) => any;
    loadProgram: (name: string, preferredWeek?: number) => any;
    setProgramSelectedWeek: (week: number) => any;
}

interface State {
    actionsAnchorElement: HTMLElement | null;
    showDeleteDialog: boolean;
}

type Props = ComponentProps & ReduxProps & ReduxActionProps & WithRouterProps;

class ProgramDetailPage extends Component<Props, State> {
    state: State = {
        actionsAnchorElement: null,
        showDeleteDialog: false,
    };

    componentDidMount() {
        this.loadCurrentProgram();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.params.name !== this.props.params.name) {
            this.loadCurrentProgram();
        }
    }

    getProgramName() {
        return String(this.props.params.name || '');
    }

    loadCurrentProgram() {
        const programName = this.getProgramName();

        if (!programName) {
            return;
        }

        this.props.loadProgram(programName);
    }

    onAddWeek = () => {
        if (!this.props.selectedProgram) {
            return;
        }

        const nextWeek = getProgramNextWeekNumber(this.props.selectedProgram.weeks);

        this.props.setProgramSelectedWeek(nextWeek);
        this.props.navigate(`/programs/${this.props.selectedProgram.name}/edit/week/${nextWeek}`);
    };

    onCloseDeleteDialog = () => {
        this.setState({ showDeleteDialog: false });
    };

    onCloseMoreActions = () => {
        this.setState({ actionsAnchorElement: null });
    };

    onConfirmDelete = async () => {
        const { selectedProgram } = this.props;

        if (!selectedProgram) {
            return;
        }

        const result = await this.props.deleteProgram(selectedProgram.name);

        if (result.success) {
            this.props.navigate('/programs');
        }
    };

    onCopyWeek = () => {
        if (!this.props.selectedProgram) {
            return;
        }

        this.props.navigate(`/programs/${this.props.selectedProgram.name}/edit/week/${this.props.selectedWeek}`);
    };

    onEditProgram = () => {
        if (!this.props.selectedProgram) {
            return;
        }

        this.props.navigate(`/programs/${this.props.selectedProgram.name}/edit`);
    };

    onOpenDeleteDialog = () => {
        this.setState({ actionsAnchorElement: null, showDeleteDialog: true });
    };

    onOpenMoreActions = (event: React.MouseEvent<HTMLElement>) => {
        this.setState({ actionsAnchorElement: event.currentTarget });
    };

    onSelectWeek = (week: number) => {
        this.props.setProgramSelectedWeek(week);
    };

    render() {
        const { deletingProgramName, loadingProgram, selectedProgram, selectedWeek } = this.props;

        if (loadingProgram && !selectedProgram) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!selectedProgram) {
            return <Typography variant="h6">Program not found</Typography>;
        }

        const deleteSummary = createProgramDeleteSummary(selectedProgram);
        const selectedProgramCatalog = this.props.selectedProgramCatalog;

        const moreActionsOpen = Boolean(this.state.actionsAnchorElement);

        return (
            <Box maxWidth={1280}>
                <Box sx={{ alignItems: { md: 'center', xs: 'stretch' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 1.5, justifyContent: 'space-between', mb: 2.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: { md: '1.6rem', xs: '1.4rem' }, fontWeight: 700, mb: 0.25 }} variant="h4">{selectedProgramCatalog?.displayName || selectedProgram.name}</Typography>
                        {selectedProgramCatalog?.description && (
                            <Typography color="text.secondary" sx={{ maxWidth: 760, mb: 0.75 }} variant="body2">
                                {selectedProgramCatalog.description}
                            </Typography>
                        )}
                        <Typography color="text.secondary" variant="body2">
                            Key: {selectedProgram.name} • {buildProgramSchedulePath(selectedProgram.name)} • {countProgramDays(selectedProgram.weeks)} of {(selectedProgramCatalog?.weeks || deleteSummary.totalWeeks) * 7} day slots filled
                        </Typography>
                        {selectedProgramCatalog && (
                            <Typography color="text.secondary" variant="body2">
                                Sort Order: {selectedProgramCatalog.sortOrder} • Completion: {PROGRAM_COMPLETION_ACTION_LABELS[selectedProgramCatalog.completionAction]}{selectedProgramCatalog.nextProgramKey ? ` • Next Program: ${selectedProgramCatalog.nextProgramKey}` : ''}
                            </Typography>
                        )}
                    </Box>
                    {selectedProgramCatalog?.imageUrl && (
                        <Box
                            alt={`${selectedProgramCatalog.displayName} thumbnail`}
                            component="img"
                            src={selectedProgramCatalog.imageUrl}
                            sx={{ borderRadius: 2, height: 112, objectFit: 'cover', width: 112 }}
                        />
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Button onClick={this.onEditProgram} startIcon={<EditOutlinedIcon />} variant="contained">Edit Program</Button>
                        <Button endIcon={<ExpandMoreRoundedIcon />} onClick={this.onOpenMoreActions} variant="outlined">More Actions</Button>
                    </Box>
                </Box>
                <Box sx={{ mb: 2.5 }}>
                    <ProgramWeekNavigator onSelectWeek={this.onSelectWeek} selectedWeek={selectedWeek} variant="detail" weeks={selectedProgram.weeks} />
                </Box>
                <Typography sx={{ fontWeight: 700, mb: 1.5 }} variant="h6">Week {selectedWeek} Overview</Typography>
                <ProgramWeekGrid readOnly selectedWeek={selectedWeek} variant="detail" weeks={selectedProgram.weeks} />
                <Menu anchorEl={this.state.actionsAnchorElement} onClose={this.onCloseMoreActions} open={moreActionsOpen}>
                    <MenuItem onClick={this.onCopyWeek}>
                        <ContentCopyRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                        Copy Week
                    </MenuItem>
                    <MenuItem onClick={this.onAddWeek}>Add Week</MenuItem>
                    <MenuItem onClick={this.onOpenDeleteDialog} sx={{ color: 'error.main' }}>
                        <DeleteOutlineRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                        Delete Program
                    </MenuItem>
                </Menu>
                <DeleteProgramDialog
                    deleting={deletingProgramName === selectedProgram.name}
                    name={selectedProgram.name}
                    onCancel={this.onCloseDeleteDialog}
                    onConfirm={this.onConfirmDelete}
                    totalDays={deleteSummary.totalDays}
                    totalWeeks={deleteSummary.totalWeeks}
                    visible={this.state.showDeleteDialog}
                />
            </Box>
        );
    }
}

const mapStateToProps: MapStateToProps<ReduxProps, ComponentProps, ReduxState> = ({ programs }: ReduxState) => ({
    deletingProgramName: programs.deletingProgramName,
    loadingProgram: programs.loadingProgram,
    selectedProgramCatalog: programs.selectedProgramCatalog,
    selectedProgram: programs.selectedProgram,
    selectedWeek: programs.selectedWeek,
});

export default connect<ReduxProps, ReduxActionProps, ComponentProps, ReduxState>(mapStateToProps, {
    deleteProgram,
    loadProgram,
    setProgramSelectedWeek,
})(withRouter(ProgramDetailPage));